import { debounce } from 'lodash-es';
import { companiesService } from '~/services/companies-service';
import { exposuresService } from '~/services/exposures-service';
import type { PublicCompany } from '~/types/person/companies';
import { getExposureStatusLabel, type CompanyExposure, type PersonExposure } from '~/types/person/details';

export const useExposuresStore = defineStore('exposures', () => {
    const personal = usePersonStore();

    const isFetching = ref(false);
    const fetchError = ref(null as null | any);
    const hasLoaded = ref(false);
    const isResetting = ref(false);

    const details = ref<CompanyExposure[]>([]);
    
    const exposures = ref<PersonExposure[]>([]);

    //pagination
    const currentPage = ref(1);
    const searchQuery = ref('');
    const selectedType = ref('');
    const selectedStatus = ref('');

    // Sorting
    const sortKey = ref<'company' | 'lastAction' | 'events' | 'active' | 'inProgress' | 'closed' | 'status'>('lastAction');
    const sortOrder = ref<'asc' | 'desc'>('desc');

    const expandedCompanyName = ref<string | null>(null);

    // External search state
    const externalSearchResults = ref<PublicCompany[]>([]);
    const isSearchingExternal = ref(false);
    const externalSearchError = ref(null as null | any);

    // Debounced search function
    const debouncedExternalSearch = debounce(async (query: string) => {
        if (query && query.length >= 3) {
            await searchExternalCompanies(query);
        }
    }, 500); // 500ms delay

    // Computed getter for filtered and paginated exposures (for details page)
    const filteredDetails = computed(() => {
        let filtered = details.value;

        // Apply search filter
        if (searchQuery.value) {
            filtered = filtered.filter((company) => 
                company.name.toLowerCase().includes(searchQuery.value.toLowerCase())
            );
        }

        // Apply type filter
        if (selectedType.value && selectedType.value !== 'all') {
            filtered = filtered.filter((company) => company.type.value === selectedType.value);
        }

        // Apply status filter
        if (selectedStatus.value && selectedStatus.value !== 'all') {
            filtered = filtered.filter((company) => {
                const statusLabel = getExposureStatusLabel(company.status);
                const selectedStatusLower = selectedStatus.value.toLowerCase();
                
                // If "exposed" is selected, include both "Exposed" and "Failed" statuses
                if (selectedStatusLower === 'exposed') {
                    return statusLabel.toLowerCase() === 'exposed' || statusLabel.toLowerCase() === 'failed';
                }
                
                // If "clean" is selected, include both "Clean" and "Closed" statuses
                if (selectedStatusLower === 'clean') {
                    return statusLabel.toLowerCase() === 'clean' || statusLabel.toLowerCase() === 'closed';
                }
                
                return statusLabel.toLowerCase() === selectedStatusLower;
            });
        }

        // Apply sorting
        const statusOrder = (status: string) => {
            // Get the display label for the status and define sort order
            const statusLabel = getExposureStatusLabel(status as any);
            const orderMap: Record<string, number> = {
                'Clean': 0,
                'Closed': 1,
                'Failed': 2,
                'Exposed': 3
            };
            return orderMap[statusLabel] ?? 4; // Unknown statuses go last
        };

        filtered.sort((a, b) => {
            let result = 0;
            switch (sortKey.value) {
                case 'company':
                    result = (a.name || '').localeCompare(b.name || '');
                    break;
                case 'lastAction':
                    // Handle 'N/A' as the oldest date
                    const getTimeOrMin = (val: string | undefined) => {
                        if (!val || val === 'N/A') return Number.MIN_SAFE_INTEGER;
                        const t = new Date(val).getTime();
                        return isNaN(t) ? Number.MIN_SAFE_INTEGER : t;
                    };
                    result = getTimeOrMin(a.last_action) - getTimeOrMin(b.last_action);
                    break;
                case 'events':
                    result = (a.events ?? 0) - (b.events ?? 0);
                    break;
                case 'active':
                    result = (a.active ?? 0) - (b.active ?? 0);
                    break;
                case 'inProgress':
                    result = (a.in_progress ?? 0) - (b.in_progress ?? 0);
                    break;
                case 'closed':
                    result = (a.closed ?? 0) - (b.closed ?? 0);
                    break;
                case 'status':
                    result = statusOrder(a.status) - statusOrder(b.status);
                    break;
            }
            return sortOrder.value === 'desc' ? -result : result;
        });

        return filtered;
    });

    // Computed getter for paginated exposures (for details page)
    const paginatedDetails = computed(() => {
        const itemsPerPage = 8;
        const startIndex = (currentPage.value - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredDetails.value.slice(startIndex, endIndex);
    });

    async function fetchDetails() {
        isFetching.value = true;
        fetchError.value = null;

        if (!personal?.person) {
            fetchError.value = 'User not found';
            isFetching.value = false;
            return;
        }

        try {
            details.value = await exposuresService.getExposureDetails(personal.person.id);
            nextTick(() => {
                hasLoaded.value = true;
            });
        } catch (error: any) {
            fetchError.value = error.message || 'Failed to fetch exposure details';
            hasLoaded.value = true; // Still mark as loaded even if there's an error
        } finally {
            isFetching.value = false;
        }
    }

    async function fetchExposures(limit: number = 50) {
        if (!personal?.person) {
            return;
        }

        try {
            exposures.value = await exposuresService.getExposures(personal.person.id, limit);
        } catch (error: any) {
            console.error('Failed to fetch raw exposures:', error);
        }
    }

    async function searchCompanies(name: string) {
        searchQuery.value = name;
        currentPage.value = 1;
        
        // Clear previous external search results
        externalSearchResults.value = [];
        externalSearchError.value = null;

        // If we have a search query with 3+ characters, check if we need to search external
        if (name && name.length >= 3) {
            // Wait for the next tick to ensure filteredDetails is updated
            await nextTick();
            
            // If no local results found OR less than 3 local results, trigger debounced external search
            if (filteredDetails.value.length === 0 || filteredDetails.value.length < 3) {
                debouncedExternalSearch(name);
            }
        } else {
            // Cancel any pending debounced search if query is too short
            debouncedExternalSearch.cancel();
        }
    }

    async function searchExternalCompanies(query: string) {
        if (!query || query.length < 3) return;

        isSearchingExternal.value = true;
        externalSearchError.value = null;

        try {
            const response = await companiesService.searchCompanies(
                query,
                10, // per_page
                1,  // page
                selectedType.value !== 'all' ? selectedType.value : ''
            );
            
            // Filter out companies that already exist in the details store
            const existingCompanyIds = details.value.map(company => company.id);
            externalSearchResults.value = (response?.data || []).filter((company: any) => !existingCompanyIds.includes(company.id)) as unknown as PublicCompany[];
        } catch (error: any) {
            externalSearchError.value = error.message || 'Failed to search external companies';
            console.error('External search failed:', error);
        } finally {
            isSearchingExternal.value = false;
        }
    }

    function clearSearch() {
        searchQuery.value = '';
        currentPage.value = 1;
        externalSearchResults.value = [];
        externalSearchError.value = null;
        // Cancel any pending debounced search
        debouncedExternalSearch.cancel();
    }

    // Helper function to reset all filters
    function resetFilters() {
        isResetting.value = true;
        searchQuery.value = '';
        selectedType.value = '';
        selectedStatus.value = '';
        currentPage.value = 1;
        externalSearchResults.value = [];
        externalSearchError.value = null;
        // Cancel any pending debounced search
        debouncedExternalSearch.cancel();

        nextTick(() => {
            isResetting.value = false;
        });
    }

    function filterByType(type: string) {
        currentPage.value = 1;
        selectedType.value = type;
        
        // If we have a search query and no local results OR less than 3 local results, trigger debounced external search
        if (searchQuery.value && searchQuery.value.length >= 3) {
            // Wait for the next tick to ensure filteredDetails is updated
            nextTick().then(() => {
                if (filteredDetails.value.length === 0 || filteredDetails.value.length < 3) {
                    debouncedExternalSearch(searchQuery.value);
                }
            });
        }
    }

    function filterByStatus(status: string) {
        currentPage.value = 1;
        selectedStatus.value = status;
    }

    function toggleExpanded(companyName: string) {
        expandedCompanyName.value = expandedCompanyName.value === companyName ? null : companyName;
    }

    function setSort(key: typeof sortKey.value) {
        if (sortKey.value === key) {
            sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc';
        } else {
            sortKey.value = key;
            sortOrder.value = 'desc';
        }
    }

    // Watch for search query changes to trigger external search
    watch(searchQuery, async (newQuery) => {
        await searchCompanies(newQuery);
    });

    return {
        isFetching,
        fetchError,
        hasLoaded,
        fetchDetails,
        fetchExposures,
        details,
        exposures,
        filteredDetails,
        paginatedDetails,
        searchCompanies,
        filterByType,
        filterByStatus,
        currentPage,
        expandedCompanyName,
        toggleExpanded,
        clearSearch,
        searchQuery,
        selectedType,
        selectedStatus,
        resetFilters,
        isResetting,
        // Sorting
        sortKey,
        sortOrder,
        setSort,
        // External search
        externalSearchResults,
        isSearchingExternal,
        externalSearchError,
        searchExternalCompanies,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useExposuresStore, import.meta.hot))
}